import {type ComponentType, type LazyExoticComponent, Suspense, useEffect, useRef, useState} from 'react'
import ScenePlaceholder from './ScenePlaceholder'

// Mounts a code-split interactive scene only once it scrolls near the viewport
// (IntersectionObserver, 300px margin). Until then — and while the scene's chunk
// is still downloading — it renders <ScenePlaceholder>, which is what the
// build-time prerender captures (no Three.js in the initial HTML/JS, and
// `[data-prerender-ready]` never waits on a scene).

export interface SceneComponentProps {
  caption: string
  aspect?: string
}

interface LazySceneProps {
  component: LazyExoticComponent<ComponentType<SceneComponentProps>>
  caption: string
  aspect?: string
}

const LazyScene = ({component: Component, caption, aspect}: LazySceneProps) => {
  const [show, setShow] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow(true)
          observer.disconnect()
        }
      },
      {rootMargin: '300px'},
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const placeholder = <ScenePlaceholder caption={caption} aspect={aspect} variant="loading" />

  return (
    <div ref={ref}>
      {show ? (
        <Suspense fallback={placeholder}>
          <Component caption={caption} aspect={aspect} />
        </Suspense>
      ) : (
        placeholder
      )}
    </div>
  )
}

export default LazyScene
